import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import posthog from "../lib/analytics";
import type { User } from "../types";
import i18n, { loadLanguage } from "../lib/i18n";

// Apply the user's saved language preference app-wide. Previously only the
// Account screen read profile.locale, so non-English users saw English on the
// dashboard and every other screen until they opened Account. Running this in
// AuthContext (which loads the user on every boot) fixes it everywhere.
//
// Monotonic sequence so only the newest call wins: loadLanguage is async, and
// without the guard a slow earlier call (or the LanguageDetector boot path)
// can clobber a later one after its awaits resolve.
let localeApplySeq = 0;

function syncDocumentLocale(lang: string) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
}

async function applyUserLocale(locale?: string | null) {
  const seq = ++localeApplySeq;
  // No saved preference: keep whatever the LanguageDetector picked, but still
  // sync dir/lang — a previous rtl session must not leak into this one.
  if (!locale) {
    syncDocumentLocale(i18n.language || "en");
    return;
  }
  if (locale === i18n.language) {
    syncDocumentLocale(locale);
    return;
  }
  try {
    // The saved profile locale wins over the detector — including switching
    // BACK to English, which the old early-return left stuck on the detected
    // language (and on rtl dir after an Arabic session).
    if (locale !== "en") await loadLanguage(locale);
    if (seq !== localeApplySeq) return;
    await i18n.changeLanguage(locale);
    if (seq !== localeApplySeq) return;
    syncDocumentLocale(locale);
  } catch { /* fall back to current language silently */ }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refetch: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true, refetch: () => {}, logout: async () => {} });

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Mirror of `user` for use inside the /me handler without a stale closure.
  // Distinguishes a cold load (never authenticated) from a refetch on an
  // already-authenticated session — the two cases handle a 429 differently.
  const userRef = useRef<User | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // refetch is handed to 6+ callers; without these guards a sustained 429 lets
  // every caller fan out its own /me + its own retry, amplifying load against
  // the very limiter we're backing off from.
  const meInFlightRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refetch = useCallback((retrying = false) => {
    if (meInFlightRef.current) return; // one /me in flight, ever
    meInFlightRef.current = true;
    setLoading(true);

    // At most one pending retry globally, regardless of how many callers hit a
    // failure. On retry-exhaustion the policy is: keep the (possibly stale)
    // user — a transient outage must never look like a logout (P2-1); the next
    // user-triggered refetch or idle-timeout re-syncs.
    const scheduleRetryOnce = () => {
      if (retrying || retryTimerRef.current) return;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        refetch(true);
      }, 1500);
    };

    fetch("/api/auth/me", { credentials: "include" })
      .then(async (r) => {
        // A transient rate-limit (429) or server hiccup must NOT be treated as
        // "logged out" — otherwise an authenticated user gets bounced to /login
        // on a brief 429 (P2-1). Keep the current session and retry once.
        if (r.status === 429 || r.status >= 500) {
          // Cold load (no user yet): keep the loading gate UP so the route
          // guard shows its spinner, not <LoginScreen>, while the retry runs.
          // Dropping it here flashed a false "logged out" screen at an
          // authenticated user on a cold load that hit a 429 — plausible for
          // several caregivers behind one agency-office IP during a morning
          // clock-in rush (P2-1 follow-up, /qa 2026-06-12). Once a user
          // exists, keep the prior behavior: release the gate and preserve the
          // stale user so a background refetch never blanks the app.
          // `retrying` = this IS the one retry; releasing the gate now avoids a
          // permanent spinner hang when a cold load 429s twice (show login,
          // the honest fallback — we genuinely can't confirm the session).
          if (userRef.current || retrying) setLoading(false);
          scheduleRetryOnce();
          return;
        }
        const data = await r.json().catch(() => ({}));
        const u = data.user || null;
        userRef.current = u;
        setUser(u);
        setLoading(false);
        if (u) {
          // Data minimization: identify by pseudonymous id only — no caregiver
          // PII to the analytics processor. Map id → email in our own DB when
          // outreach needs it. (Eng-review P2 compliance item, 2026-06-09.)
          posthog.identify(u.id);
          applyUserLocale(u.locale);
        }
      })
      .catch(() => {
        // Network blip: don't nuke an existing session; retry once.
        setLoading(false);
        scheduleRetryOnce();
      })
      .finally(() => {
        meInFlightRef.current = false;
      });
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // continue even if request fails
    }
    userRef.current = null;
    setUser(null);
  }, []);

  useEffect(() => {
    refetch();
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [refetch]);

  // Client-side idle timeout — logs user out after 15 min of inactivity
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logout().then(() => {
          window.location.href = "/login";
        });
      }, IDLE_TIMEOUT_MS);
    };

    const events = ["mousemove", "keydown", "touchstart", "click"] as const;
    events.forEach(evt => window.addEventListener(evt, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, logout]);

  return <AuthContext.Provider value={{ user, loading, refetch, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
